import * as React from "react";
import { 
  Play, Loader2, CheckCircle, XCircle, Clock, BarChart3,
  RefreshCw, Database, TrendingUp, AlertTriangle, ChevronDown, ChevronRight, Ban
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ExcludedAccountsCard = ({ excluded, count }) => {
  const [expanded, setExpanded] = React.useState(false);
  
  if (!excluded || excluded.length === 0) return null;
  
  return (
    <Card>
      <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Ban className="h-4 w-4 text-amber-500" />
            Cuentas excluidas del baseline
            <Badge variant="outline" className="text-amber-600 border-amber-300 ml-1">{count}</Badge>
          </CardTitle>
          {expanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
        </div>
        <CardDescription>
          Cuentas con datos insuficientes para calcular un baseline confiable
        </CardDescription>
      </CardHeader>
      {expanded && (
        <CardContent className="pt-0">
          <div className="max-h-[400px] overflow-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[30%]">Cuenta</TableHead>
                  <TableHead className="w-[15%]">Cliente</TableHead>
                  <TableHead className="w-[12%]">Plataforma</TableHead>
                  <TableHead className="w-[8%] text-right">Días</TableHead>
                  <TableHead className="w-[10%] text-right">Avg $/día</TableHead>
                  <TableHead className="w-[10%] text-right">Total $</TableHead>
                  <TableHead className="w-[15%]">Razón</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {excluded.map((acc) => (
                  <TableRow key={acc.account_id} className="text-xs">
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-700 truncate">{acc.account_name || acc.account_id}</p>
                        <p className="text-[10px] text-slate-400">{acc.account_id}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">{acc.cliente}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">{acc.platform}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">{acc.days}</TableCell>
                    <TableCell className="text-right font-mono">${acc.avg_spend?.toLocaleString()}</TableCell>
                    <TableCell className="text-right font-mono">${acc.total_spend?.toLocaleString()}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {acc.reasons?.map((r, i) => (
                          <Badge key={i} className="text-[9px] bg-amber-50 text-amber-700 border-amber-200">{r}</Badge>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export const AnomalyV2Admin = () => {
  const [jobStatus, setJobStatus] = React.useState(null);
  const [summary, setSummary] = React.useState(null);
  const [isRunning, setIsRunning] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const pollRef = React.useRef(null);
  
  // Detection state
  const [detectionStatus, setDetectionStatus] = React.useState(null);
  const [detectionSummary, setDetectionSummary] = React.useState(null);
  const [isDetecting, setIsDetecting] = React.useState(false);
  const detectPollRef = React.useRef(null);

  React.useEffect(() => {
    loadData();
    return () => { 
      if (pollRef.current) clearInterval(pollRef.current);
      if (detectPollRef.current) clearInterval(detectPollRef.current);
    };
  }, []);

  const loadData = async () => {
    try {
      const [statusRes, summaryRes, detStatusRes, detSummaryRes] = await Promise.all([
        axios.get(`${API}/admin/anomaly-v2/job-status`, { withCredentials: true }),
        axios.get(`${API}/admin/anomaly-v2/baseline-summary`, { withCredentials: true }),
        axios.get(`${API}/admin/anomaly-v2/detection-status`, { withCredentials: true }),
        axios.get(`${API}/admin/anomaly-v2/detection-summary`, { withCredentials: true })
      ]);
      setJobStatus(statusRes.data);
      setSummary(summaryRes.data);
      setDetectionStatus(detStatusRes.data);
      setDetectionSummary(detSummaryRes.data);
      if (statusRes.data.status === "running") {
        setIsRunning(true);
        startPolling();
      }
    } catch (error) {
      console.error("Error loading anomaly v2 data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/admin/anomaly-v2/job-status`, { withCredentials: true });
        setJobStatus(res.data);
        if (res.data.status !== "running") {
          setIsRunning(false);
          clearInterval(pollRef.current);
          pollRef.current = null;
          // Reload summary
          const sumRes = await axios.get(`${API}/admin/anomaly-v2/baseline-summary`, { withCredentials: true });
          setSummary(sumRes.data);
          if (res.data.status === "completed") {
            toast.success("Baseline calculado exitosamente");
          } else {
            toast.error("Error en el cálculo de baseline");
          }
        }
      } catch (e) { /* silent */ }
    }, 3000);
  };

  const handleRunJob = async () => {
    setIsRunning(true);
    try {
      await axios.post(`${API}/admin/anomaly-v2/run-baseline`, {}, { withCredentials: true });
      toast.info("Job de baseline iniciado...");
      startPolling();
    } catch (error) {
      setIsRunning(false);
      toast.error(error.response?.data?.detail || "Error al iniciar el job");
    }
  };

  const startDetectionPolling = () => {
    if (detectPollRef.current) clearInterval(detectPollRef.current);
    detectPollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/admin/anomaly-v2/detection-status`, { withCredentials: true });
        setDetectionStatus(res.data);
        if (res.data.status !== "running") {
          setIsDetecting(false);
          clearInterval(detectPollRef.current);
          detectPollRef.current = null;
          const sumRes = await axios.get(`${API}/admin/anomaly-v2/detection-summary`, { withCredentials: true });
          setDetectionSummary(sumRes.data);
          if (res.data.status === "completed") {
            toast.success("Detección de anomalías completada");
          } else {
            toast.error("Error en la detección");
          }
        }
      } catch (e) { /* silent */ }
    }, 3000);
  };

  const handleRunDetection = async () => {
    setIsDetecting(true);
    try {
      await axios.post(`${API}/admin/anomaly-v2/run-detection`, {}, { withCredentials: true });
      toast.info("Job de detección iniciado...");
      startDetectionPolling();
    } catch (error) {
      setIsDetecting(false);
      toast.error(error.response?.data?.detail || "Error al iniciar la detección");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const lastResult = jobStatus?.result;
  const hasData = summary?.total_stats > 0;

  return (
    <div className="space-y-6">
      {/* Run Job Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Database className="h-4 w-4 text-indigo-500" />
                Cálculo de Baseline
              </CardTitle>
              <CardDescription className="mt-1">
                Calcula estadísticas base (media, stddev, umbrales) de los últimos 60 días, segmentado por día de la semana.
              </CardDescription>
            </div>
            <Button
              onClick={handleRunJob}
              disabled={isRunning}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="run-baseline-btn"
            >
              {isRunning ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Calculando...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Recalcular Baseline
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        {jobStatus && jobStatus.status !== "never_run" && (
          <CardContent className="pt-0">
            <div className={`p-3 rounded-lg border text-sm ${
              jobStatus.status === "running" ? "bg-blue-50 border-blue-200" :
              jobStatus.status === "completed" ? "bg-emerald-50 border-emerald-200" :
              jobStatus.status === "error" ? "bg-red-50 border-red-200" :
              "bg-slate-50 border-slate-200"
            }`}>
              <div className="flex items-center gap-2 mb-1">
                {jobStatus.status === "running" && <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
                {jobStatus.status === "completed" && <CheckCircle className="h-4 w-4 text-emerald-600" />}
                {jobStatus.status === "error" && <XCircle className="h-4 w-4 text-red-600" />}
                <span className="font-medium capitalize">{
                  jobStatus.status === "running" ? "En ejecución..." :
                  jobStatus.status === "completed" ? "Completado" : 
                  jobStatus.status === "error" ? "Error" : jobStatus.status
                }</span>
                {jobStatus.started_by && (
                  <span className="text-xs text-slate-500">por {jobStatus.started_by}</span>
                )}
              </div>
              {lastResult && (
                <div className="text-xs text-slate-600 space-y-0.5">
                  {lastResult.message && <p>{lastResult.message}</p>}
                  {lastResult.duration_seconds && <p>Duración: {lastResult.duration_seconds}s</p>}
                  {lastResult.stats_created > 0 && (
                    <p>Stats: {lastResult.stats_created} ({lastResult.active_stats} activas, {lastResult.inactive_stats} inactivas)</p>
                  )}
                </div>
              )}
              {jobStatus.completed_at && (
                <p className="text-xs text-slate-400 mt-1">
                  {new Date(jobStatus.completed_at).toLocaleString("es-ES")}
                </p>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Summary */}
      {hasData ? (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-slate-800">{summary.total_stats?.toLocaleString()}</p>
                <p className="text-xs text-slate-500 mt-1">Stats Totales</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-indigo-600">{summary.unique_accounts}</p>
                <p className="text-xs text-slate-500 mt-1">Cuentas</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-emerald-600">
                  {summary.by_status?.find(s => s.is_active)?.count?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-slate-500 mt-1">Activas</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-amber-600">
                  {summary.by_status?.find(s => !s.is_active)?.count?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-slate-500 mt-1">Inactivas</p>
              </CardContent>
            </Card>
          </div>

          {/* By Metric */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-indigo-500" />
                Stats por Métrica
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {summary.by_metric?.map(m => (
                  <div key={m.metric_name} className="p-3 rounded-lg bg-slate-50 border">
                    <p className="text-sm font-medium text-slate-700">{m.metric_name}</p>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="text-lg font-bold text-slate-800">{m.count}</span>
                      <span className="text-xs text-emerald-600">{m.active} activas</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* By Variability */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-indigo-500" />
                Distribución de Variabilidad
              </CardTitle>
              <CardDescription>
                Determina los umbrales adaptativos de Z-Score para cada segmento
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3 flex-wrap">
                {summary.by_variability?.map(v => {
                  const colors = {
                    low: "bg-emerald-100 text-emerald-700 border-emerald-200",
                    medium: "bg-blue-100 text-blue-700 border-blue-200",
                    high: "bg-amber-100 text-amber-700 border-amber-200",
                    very_high: "bg-red-100 text-red-700 border-red-200",
                    unknown: "bg-slate-100 text-slate-600 border-slate-200"
                  };
                  const thresholds = {
                    low: "W:1.5 / C:2.5",
                    medium: "W:2.0 / C:3.0",
                    high: "W:2.5 / C:3.5",
                    very_high: "W:3.0 / C:4.0"
                  };
                  return (
                    <div key={v.variability_segment} className={`px-4 py-3 rounded-lg border ${colors[v.variability_segment] || colors.unknown}`}>
                      <p className="font-medium text-sm capitalize">{v.variability_segment || "N/A"}</p>
                      <p className="text-xl font-bold">{v.count?.toLocaleString()}</p>
                      {thresholds[v.variability_segment] && (
                        <p className="text-[10px] mt-0.5 opacity-75">{thresholds[v.variability_segment]}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Excluded Accounts */}
          {lastResult?.excluded_accounts > 0 && (
            <ExcludedAccountsCard excluded={lastResult.excluded_details} count={lastResult.excluded_accounts} />
          )}

          {/* Last Updated */}
          {summary.last_updated_range?.newest && (
            <p className="text-xs text-slate-400 text-right">
              Última actualización baseline: {new Date(summary.last_updated_range.newest).toLocaleString("es-ES")}
            </p>
          )}

          {/* === DETECTION SECTION === */}
          <div className="border-t border-slate-200 pt-6 mt-2">
            {/* Detection Job Card */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-orange-500" />
                      Detección de Anomalías
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Compara los datos de ayer contra el baseline y detecta desviaciones significativas.
                    </CardDescription>
                  </div>
                  <Button
                    onClick={handleRunDetection}
                    disabled={isDetecting || !hasData}
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid="run-detection-btn"
                  >
                    {isDetecting ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Detectando...</>
                    ) : (
                      <><Play className="h-4 w-4 mr-2" />Ejecutar Detección</>
                    )}
                  </Button>
                </div>
              </CardHeader>
              {detectionStatus && detectionStatus.status !== "never_run" && (
                <CardContent className="pt-0">
                  <div className={`p-3 rounded-lg border text-sm ${
                    detectionStatus.status === "running" ? "bg-blue-50 border-blue-200" :
                    detectionStatus.status === "completed" ? "bg-emerald-50 border-emerald-200" :
                    "bg-red-50 border-red-200"
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      {detectionStatus.status === "running" && <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
                      {detectionStatus.status === "completed" && <CheckCircle className="h-4 w-4 text-emerald-600" />}
                      {detectionStatus.status === "error" && <XCircle className="h-4 w-4 text-red-600" />}
                      <span className="font-medium">{
                        detectionStatus.status === "running" ? "En ejecución..." :
                        detectionStatus.status === "completed" ? "Completado" : "Error"
                      }</span>
                    </div>
                    {detectionStatus.result && (
                      <div className="text-xs text-slate-600 space-y-0.5">
                        <p>{detectionStatus.result.message}</p>
                        {detectionStatus.result.status_breakdown && (
                          <p>
                            NORMAL: {detectionStatus.result.status_breakdown.NORMAL || 0} | 
                            WARNING: {detectionStatus.result.status_breakdown.WARNING || 0} | 
                            CRITICAL: {detectionStatus.result.status_breakdown.CRITICAL || 0}
                          </p>
                        )}
                        {detectionStatus.result.duration_seconds && <p>Duración: {detectionStatus.result.duration_seconds}s</p>}
                      </div>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Detection Results */}
            {detectionSummary?.has_data && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-slate-800">{detectionSummary.total_results}</p>
                      <p className="text-xs text-slate-500 mt-1">Resultados ({detectionSummary.detection_date})</p>
                    </CardContent>
                  </Card>
                  {detectionSummary.by_status?.map(s => {
                    const c = { CRITICAL: "text-red-600", WARNING: "text-amber-600", NORMAL: "text-emerald-600" };
                    return (
                      <Card key={s.anomaly_status}>
                        <CardContent className="p-4 text-center">
                          <p className={`text-2xl font-bold ${c[s.anomaly_status] || "text-slate-600"}`}>{s.count}</p>
                          <p className="text-xs text-slate-500 mt-1">{s.anomaly_status}</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Top Anomalies Table */}
                {detectionSummary.top_anomalies?.length > 0 && (
                  <Card className="mt-4">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                        Top Anomalías (WARNING + CRITICAL)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="max-h-[400px] overflow-auto rounded-lg border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Cliente / Cuenta</TableHead>
                              <TableHead>Métrica</TableHead>
                              <TableHead className="text-right">Real</TableHead>
                              <TableHead className="text-right">Esperado</TableHead>
                              <TableHead className="text-right">Z-Score</TableHead>
                              <TableHead className="text-right">% Cambio</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {detectionSummary.top_anomalies.map((a, i) => (
                              <TableRow key={i} className="text-xs">
                                <TableCell>
                                  <p className="font-medium text-slate-700">{a.account_name}</p>
                                  <p className="text-[10px] text-slate-400">{a.cliente}</p>
                                </TableCell>
                                <TableCell className="font-mono">{a.metric_name}</TableCell>
                                <TableCell className="text-right font-mono">{a.actual_value?.toLocaleString()}</TableCell>
                                <TableCell className="text-right font-mono text-slate-500">{a.expected_value?.toLocaleString()}</TableCell>
                                <TableCell className="text-right font-mono font-bold">
                                  <span className={a.anomaly_status === "CRITICAL" ? "text-red-600" : "text-amber-600"}>
                                    {a.z_score > 0 ? "+" : ""}{a.z_score}
                                  </span>
                                </TableCell>
                                <TableCell className="text-right font-mono">
                                  <span className={a.pct_change > 0 ? "text-red-500" : "text-blue-500"}>
                                    {a.pct_change > 0 ? "+" : ""}{a.pct_change}%
                                  </span>
                                </TableCell>
                                <TableCell>
                                  <Badge className={`text-[10px] ${
                                    a.anomaly_status === "CRITICAL" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
                                  }`}>
                                    {a.anomaly_status} {a.anomaly_direction === "INCREASE" ? "↑" : "↓"}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        </>
      ) : (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Sin datos de baseline</AlertTitle>
          <AlertDescription className="text-amber-700">
            Ejecuta el cálculo de baseline para generar las estadísticas base de anomalías.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
